import React, { useEffect, useState } from 'react';
import './ViewAsContactSearch.css';

function ViewAsContactSearch({ value, onChange, authenticatedEmail }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const query = value.trim();
  const canSearch = query.length >= 2 && query.length <= 100;

  useEffect(() => {
    if (!open || !canSearch) return;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError('');
    setContacts([]);
    setActiveIndex(-1);
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`/api/salesforce-data?contactSearch=${encodeURIComponent(query)}`, {
          headers: { 'X-Email-Header': authenticatedEmail },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Contact search failed (HTTP ${response.status}). Please try again.`);
        const result = await response.json();
        if (!Array.isArray(result.contacts)) throw new Error('Invalid contact search response. Please try again.');
        if (!cancelled) setContacts(result.contacts);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, canSearch, query, authenticatedEmail]);

  const selectContact = contact => {
    onChange(contact.Email);
    setOpen(false);
  };
  const showResults = open && canSearch;

  return (
    <div className="view-as-contact-search">
      <input
        id="view-as-contact"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showResults}
        aria-controls={showResults ? 'view-as-contact-results' : undefined}
        aria-activedescendant={showResults && activeIndex >= 0 ? `view-as-contact-${activeIndex}` : undefined}
        aria-describedby="view-as-contact-help"
        autoComplete="off"
        maxLength={254}
        value={value}
        onChange={event => {
          onChange(event.target.value);
          setContacts([]);
          setActiveIndex(-1);
          setLoading(true);
          setError('');
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={event => {
          if (event.key === 'Escape') setOpen(false);
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            if (contacts.length) {
              setActiveIndex(index => event.key === 'ArrowDown'
                ? (index + 1) % contacts.length
                : (index <= 0 ? contacts.length : index) - 1);
            }
          }
          if (event.key === 'Enter' && showResults && contacts[activeIndex]) {
            event.preventDefault();
            selectContact(contacts[activeIndex]);
          }
        }}
        placeholder="user@domain.com"
      />
      <small id="view-as-contact-help">Search by contact name or email (at least 2 characters).</small>
      {showResults && (
        <div className="view-as-contact-dropdown">
          <ul id="view-as-contact-results" role="listbox" aria-label="Roster contacts" aria-busy={loading}>
            {contacts.map((contact, index) => (
              <li
                key={contact.Id}
                id={`view-as-contact-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={event => event.preventDefault()}
                onClick={() => selectContact(contact)}
              >
                <strong>{contact.Name}</strong>
                <span>{contact.Email}</span>
              </li>
            ))}
          </ul>
          {loading && <p role="status">Searching contacts...</p>}
          {error && <p role="alert">{error}</p>}
          {!loading && !error && !contacts.length && <p role="status">No matching roster contacts on portal-enabled projects.</p>}
        </div>
      )}
    </div>
  );
}

export default ViewAsContactSearch;
